import {
  Button,
  ConfirmAlert,
  Loading,
  Separator,
  Spacer,
} from '@carboncopyco/ui';

import {
  RecorderModes,
  RecordingGuideModal,
  isDisabledRecordingGuideModal,
} from '@carboncopyco/components';
import {
  DeleteOutlineIcon,
  RecordingIcon,
  StopIcon,
} from '@carboncopyco/icons';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useEffect, useState } from 'react';
import { useTabFocus } from '../../hooks/useTabFocus';
import ActiveUrlDetails from '../ActiveUrlDetails';
import ModalWrapper from '../Modals/ModalWrapper';
import NavigationBar from '../Navigation/NavigationBar';
import { SaveRecording } from '../SaveRecording/SaveRecording';
import StatusIndicator from './StatusIndicator';

import rendererService from '../../services/RendererService';
import { useTabManager } from '../../services/TabManager';
import { isActiveOrAllInnerTabsLoaded } from '../../shared/utils';
import { useTabs } from '../../store/context';
import Navigation from '../Navigation/Navigation';
import { UpdateRecording } from '../UpdateRecording/UpdateRecording';
import StartRecordingTimeLeftText from './StartRecordingTimeLeftText';

interface StartRecordingProps {
  // isStopDropdownOpened: boolean;
  updateRecording: boolean;
  isRecording: boolean;
  isLoading: boolean;
  onStartRecording: () => void;
  onSavedRecordingSubmitted: (data: any) => void;
  // onToggleStopDropdown: () => void;
  onExitRecorder: () => void;
}

export const StartRecording = ({
  updateRecording,
  onSavedRecordingSubmitted,
  isRecording,
  isLoading,
  onStartRecording,
  onExitRecorder,
}: StartRecordingProps) => {
  const [isStopDropdownOpened, setIsStopDropdownOpened] = useState(false);
  const [pageTitle, setPageTitle] = useState('');
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);
  const tab = useTabManager().getActiveGlobalTab();
  const { trackEvent } = useAnalytics();
  const { isOpenCloseTabDialog, setIsOpenCloseTabDialog } = useTabs();
  useTabFocus({
    tab,
    onTabIsDeactivated: () => {
      setIsOpenCloseTabDialog(false);
    },
  });

  const recorderMode = updateRecording
    ? RecorderModes.updateRecording
    : RecorderModes.record;

  useEffect(() => {
    if (!isRecording && !isLoading && isStopDropdownOpened) {
      setIsStopDropdownOpened(false);
    }
    if (isStopDropdownOpened) {
      window.dsDesktop?.recorder
        .getRecordingPageTitle(tab.activeInnerTabId)
        // eslint-disable-next-line promise/always-return
        .then((title: string) => {
          setPageTitle(title);
        });
      rendererService.focusApp();
    } else if (isRecording) {
      rendererService.blurApp();
    }
  }, [isRecording, isLoading, isStopDropdownOpened]);

  const onCancel = () => {
    trackEvent(AnalyticsTrackEventName.CLONE_CLICK_CANCEL, {
      location: 'cloning',
    });
    setIsOpenCloseTabDialog(false);
  };

  const handleSaveRecordingSubmit = (data: any) => {
    onSavedRecordingSubmitted(data);
  };

  const handleStopCloning = () => {
    setIsStopDropdownOpened(true);
    trackEvent(AnalyticsTrackEventName.CLONE_CLICK_STOP_CLONING, {
      location: 'cloning',
    });
  };

  return (
    <div className="w-full">
      <Navigation
        showDashboardButton
        disabledUserMenu
      >
        <>
          <div className="flex align-center w-full height-36 ">
            <Separator middle />
            <Spacer amount={1.5} />
            <div className="flex justify-center align-center">
              <div>
                <NavigationBar tab={tab} />
              </div>
              <div>
                <Spacer amount={1.5} />
              </div>
              <div className="url-container flex justify-between">
                <StatusIndicator isActive={isRecording} />
                <Spacer amount={1.5} />
                <ActiveUrlDetails />
              </div>
            </div>
          </div>
          <div className="w-full flex">
            <div className="flex w-full justify-end">
              <div className="mr-2">
                <Button
                  link
                  secondary
                  label="Exit"
                  onClick={() => {
                    trackEvent(AnalyticsTrackEventName.CLONE_CLICK_EXIT, {
                      location: 'cloning',
                    });
                    setIsOpenCloseTabDialog(true);
                  }}
                />
              </div>
              {isRecording ? (
                <Button
                  danger
                  leftIcon={<StopIcon />}
                  label="Stop"
                  onClick={handleStopCloning}
                />
              ) : (
                <>
                  <Button
                    leftIcon={
                      isLoading ? (
                        <Loading color="text-current" />
                      ) : (
                        <RecordingIcon />
                      )
                    }
                    blue={false}
                    label={
                      updateRecording ? 'Continue cloning' : 'Start cloning'
                    }
                    onClick={() =>
                      isDisabledRecordingGuideModal(recorderMode)
                        ? onStartRecording()
                        : setIsGuideModalOpen(true)
                    }
                    disabled={!isActiveOrAllInnerTabsLoaded(tab) || isLoading}
                  />
                  <ModalWrapper
                    isVisible={isGuideModalOpen}
                    onCloseModal={() => setIsGuideModalOpen(false)}
                  >
                    <RecordingGuideModal
                      onClose={() => {
                        setIsGuideModalOpen(false);
                      }}
                      onConfirm={() => {
                        setIsGuideModalOpen(false);
                        onStartRecording();
                      }}
                      isOpen={isGuideModalOpen}
                      mode={recorderMode}
                      timeLimitText={
                        <StartRecordingTimeLeftText
                          cloneId={updateRecording ? tab.cloneId : ''}
                          className="mt-2.5 -mx-1"
                        />
                      }
                    />
                  </ModalWrapper>
                </>
              )}

              <div>
                <Spacer amount={1.5} />
              </div>
              <Separator />
              <Spacer amount={1.5} />
            </div>
          </div>
        </>
      </Navigation>
      <div>
        {isStopDropdownOpened ? (
          <div className="stop-modal-container">
            <div className="stop-modal-inner">
              <div className="pt-1">
                <ModalWrapper
                  isVisible={isStopDropdownOpened}
                  onCloseModal={() => setIsStopDropdownOpened(false)}
                  isAppInFocus
                >
                  {updateRecording ? (
                    <UpdateRecording
                      onCancel={() => setIsStopDropdownOpened(false)}
                      submitForm={handleSaveRecordingSubmit}
                      isLoading={isLoading}
                    />
                  ) : (
                    <SaveRecording
                      onCancel={() => {
                        trackEvent(AnalyticsTrackEventName.CLONE_CLICK_CANCEL, {
                          location: 'live_editor',
                        });
                        rendererService.blurApp();
                        setIsStopDropdownOpened(false);
                      }}
                      recordingStarted
                      curTitle={pageTitle}
                      submitForm={handleSaveRecordingSubmit}
                      isLoading={isLoading}
                      folderId={tab.folderId}
                    />
                  )}
                </ModalWrapper>
              </div>
            </div>
          </div>
        ) : null}
        <ModalWrapper
          isVisible={isOpenCloseTabDialog}
          onCloseModal={() => setIsOpenCloseTabDialog(false)}
        >
          <ConfirmAlert
            containerClass="w-ds-500"
            show={isOpenCloseTabDialog}
            onConfirm={() => {
              setIsOpenCloseTabDialog(false);
              onExitRecorder();
            }}
            onClose={onCancel}
            title="Discard changes"
            description={
              updateRecording
                ? 'Discard your changes and exit cloning?'
                : 'Discard your progress and exit cloning?'
            }
            yesLabel="Discard and exit"
            noLabel="Cancel"
            danger
            yesIcon={<DeleteOutlineIcon />}
          />
        </ModalWrapper>
      </div>
    </div>
  );
};
